import { useMemo, useState, type JSX } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { useLiveQuery } from "dexie-react-hooks";
import { collectPlanRecipeIds, type MealPlanApiItem } from "@recipevault/shared";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/ui/sheet.js";
import { Button } from "@/ui/button.js";
import { Input } from "@/ui/input.js";
import { Label } from "@/ui/label.js";
import { db } from "../../sync/db.js";
import { sync } from "../../sync/SyncService.js";
import { friendlyError } from "../../shared/http.js";

export interface BuildShoppingListSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The week currently being viewed — the shopping list's source. */
  plan: MealPlanApiItem;
}

const WEEK_LABEL_FORMATTER = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

/** Human label for an ISO calendar date, e.g. "2026-07-06" -> "Jul 6". Anchored at UTC
 * noon (matches DaySection's weekdayLabel) so no local timezone ever rolls it to the
 * adjacent day. */
function weekLabel(isoDate: string): string {
  const [y, m, d] = isoDate.split("-").map((v) => Number.parseInt(v, 10)) as [number, number, number];
  return WEEK_LABEL_FORMATTER.format(new Date(Date.UTC(y, m - 1, d, 12)));
}

function defaultName(weekStartDate: string): string {
  return `Week of ${weekLabel(weekStartDate)}`;
}

type Mode = "new" | "existing";

/**
 * "Build a shopping list from this week" (Task G6) — either a brand-new list, or merged
 * into an existing ACTIVE one. Sourced from `collectPlanRecipeIds` (shared, Task A3):
 * unique recipe ids -> number of separate cooks this week, excluding leftover/skipped
 * slots and freetext dishes (those don't feed shopping lists — that's Slice D's job).
 *
 * Creating a NEW list has no merge risk, so "Create list" submits directly. Adding to an
 * EXISTING list merges quantities into whatever's already there, so it's gated behind an
 * explicit confirm step: picking a list from the row just previews the merge — the
 * mutation only fires when "Add to list" is clicked separately.
 */
export function BuildShoppingListSheet({ open, onOpenChange, plan }: BuildShoppingListSheetProps): JSX.Element {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>("new");
  const [name, setName] = useState(() => defaultName(plan.weekStartDate));
  const [selectedListId, setSelectedListId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const activeLists = useLiveQuery(
    () => db.shoppingLists.where("status").equals("active").toArray(),
    [],
  );

  const { recipeIds, scaleByServings } = useMemo(() => {
    const counts = collectPlanRecipeIds(plan);
    return { recipeIds: Array.from(counts.keys()), scaleByServings: Object.fromEntries(counts) };
  }, [plan]);

  const hasRecipes = recipeIds.length > 0;
  const selectedList = (activeLists ?? []).find((l) => l.id === selectedListId) ?? null;

  function reset(): void {
    setMode("new");
    setName(defaultName(plan.weekStartDate));
    setSelectedListId(null);
  }

  function handleOpenChange(next: boolean): void {
    if (!next) reset();
    onOpenChange(next);
  }

  async function handleCreateNew(): Promise<void> {
    const trimmed = name.trim();
    if (!trimmed || !hasRecipes || busy) return;
    setBusy(true);
    try {
      const list = await sync.createShoppingListOptimistic({ name: trimmed, recipeIds, scaleByServings });
      handleOpenChange(false);
      void navigate(`/shopping/${list.id}`);
    } catch (e) {
      toast.error(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleConfirmAdd(): Promise<void> {
    if (!selectedListId || !hasRecipes || busy) return;
    setBusy(true);
    try {
      await sync.addItemOptimistic(selectedListId, { fromRecipeIds: recipeIds, scaleByServings });
      handleOpenChange(false);
    } catch (e) {
      toast.error(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }

  const recipeCountLabel = `${recipeIds.length} recipe${recipeIds.length === 1 ? "" : "s"}`;

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Build shopping list</SheetTitle>
          <SheetDescription>
            {hasRecipes
              ? `Adds ${recipeCountLabel} from this week.`
              : "This week has no recipe dishes yet — free-text meals and leftovers don't feed shopping lists."}
          </SheetDescription>
        </SheetHeader>

        {!hasRecipes && (
          <div className="px-4 pb-4">
            <Button type="button" disabled>
              Build shopping list
            </Button>
          </div>
        )}

        {hasRecipes && (
          <>
            <div className="flex gap-2 px-4">
              <Button
                type="button"
                variant={mode === "new" ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  setMode("new");
                }}
              >
                New list
              </Button>
              <Button
                type="button"
                variant={mode === "existing" ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  setMode("existing");
                }}
              >
                Add to an existing list
              </Button>
            </div>

            {mode === "new" && (
              <div className="flex flex-col gap-4 px-4 pb-4 mt-4">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="build-list-name">List name</Label>
                  <Input
                    id="build-list-name"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                    }}
                    maxLength={120}
                  />
                </div>
                <SheetFooter>
                  <Button
                    type="button"
                    onClick={() => {
                      void handleCreateNew();
                    }}
                    disabled={!name.trim() || busy}
                  >
                    {busy ? "Creating…" : "Create list"}
                  </Button>
                </SheetFooter>
              </div>
            )}

            {mode === "existing" && (
              <div className="flex flex-col gap-1 px-4 pb-4 mt-4">
                {!activeLists || activeLists.length === 0 ? (
                  <p className="text-sm text-zinc-500">No active lists yet — create a new one instead.</p>
                ) : (
                  activeLists.map((list) => {
                    const total = list.items.length;
                    return (
                      <label
                        key={list.id}
                        className="flex items-center justify-between gap-2 rounded-xl border border-zinc-800 bg-zinc-900 px-4 py-3 cursor-pointer hover:bg-zinc-800/60"
                      >
                        <span className="flex items-center gap-2 min-w-0">
                          <input
                            type="radio"
                            name="build-shopping-list-target"
                            checked={selectedListId === list.id}
                            onChange={() => {
                              setSelectedListId(list.id);
                            }}
                            className="accent-emerald-500"
                          />
                          <span className="font-medium text-zinc-100 truncate">{list.name}</span>
                        </span>
                        <span className="text-xs text-zinc-500 shrink-0">
                          {total === 0 ? "No items" : `${total} item${total === 1 ? "" : "s"}`}
                        </span>
                      </label>
                    );
                  })
                )}

                {selectedList && (
                  <div className="mt-3 flex flex-col gap-2 rounded-md border border-zinc-800 bg-zinc-900/60 p-3">
                    <p className="text-sm text-zinc-300">
                      Adds {recipeCountLabel} to &quot;{selectedList.name}&quot; — merges into existing items,
                      quantities increase.
                    </p>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => {
                        void handleConfirmAdd();
                      }}
                      disabled={busy}
                    >
                      {busy ? "Adding…" : "Add to list"}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
